<template>
  <div class="uplift-chart-container">
    <mf-info-card v-if="showInfoCard" variant="info" :v-html="infoCardHtml" class="mt-5" />
    <mf-tabs-menu v-model="tab" :tabs="tabItems" class="my-4" />
    <div class="apex-chart-section">
      <div v-if="previousPeriodData.length && offeredPeriodData.length" :style="{ width: apexChartWidth, minWidth: '100%' }">
        <apexchart width="100%" type="bar" :options="options" :series="series" height="292px" />
      </div>
      <chart-empty-state v-else />
    </div>
  </div>
</template>

<script>
import { localeString, abbreviation } from '@/config/helpers.js'
import ChartEmptyState from '@/components/campaigns/ChartEmptyState.vue'

export default {
  name: 'UpliftChart',
  components: {
    ChartEmptyState,
  },
  props: {
    metrics: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      tab: 0,
      tabItems: [
        {
          name: 'Faturamento',
          itemsLength: 0,
          haveLength: false,
          tooltip: this.$t('campanhas.gestao_de_ofertas.results.uplift.tooltips.revenue'),
        },
        {
          name: 'Unidades vendidas',
          itemsLength: 0,
          haveLength: false,
          tooltip: this.$t('campanhas.gestao_de_ofertas.results.uplift.tooltips.quantity'),
        },
        {
          name: 'Número de cupons',
          itemsLength: 0,
          haveLength: false,
          tooltip: this.$t('campanhas.gestao_de_ofertas.results.uplift.tooltips.coupons'),
        },
        {
          name: 'Consumidores',
          itemsLength: 0,
          haveLength: false,
          tooltip: this.$t('campanhas.gestao_de_ofertas.results.uplift.tooltips.consumers'),
        },
      ],
      tabsValues: ['revenue', 'quantity', 'tickets', 'clients'],
    }
  },
  computed: {
    showInfoCard() {
      return this.metrics?.length > 100
    },
    infoCardHtml() {
      return '<span style="color: #004D99"><b>Essa oferta contém mais de 100 produtos!</b> O gráfico abaixo tem um limite de exibição de 100 colunas, para analisar o uplift completo de todos os produtos ofertados, você pode <a id="export-data">Exportar dados por produtos</a>.</span>'
    },
    options() {
      const useFormatter = this.tab
      return {
        xaxis: {
          categories: this.formattedProducts,
          labels: {
            rotate: 0,
            trim: true,
            hideOverlappingLabels: false,
          },
          crosshairs: {
            show: false,
            width: 1,
          },
        },
        yaxis: {
          labels: {
            formatter: function (value) {
              return !useFormatter ? `R$ ${abbreviation(value, { isCurrency: true })}` : value.toFixed(0)
            },
          },
        },
        legend: {
          horizontalAlign: 'left',
          position: 'bottom',
        },
        plotOptions: {
          bar: {
            columnWidth: '90%',
            dataLabels: {
              position: 'top',
            },
          },
        },
        dataLabels: {
          background: {
            enabled: true,
            foreColor: '#555',
          },
          offsetY: -20,
          formatter: function (value, _) {
            return !useFormatter ? `R$ ${abbreviation(value, { isCurrency: true })}` : localeString(value.toFixed(0), 0)
          },
        },
        colors: ['#002040', '#00B395'],
        tooltip: {
          shared: true,
          intersect: false,
          followCursor: true,
          ...this.tooltip,
        },
      }
    },
    series() {
      return [
        {
          name: 'Período anterior',
          data: this.previousPeriodData,
        },
        {
          name: 'Período ofertado',
          data: this.offeredPeriodData,
        },
      ]
    },
    apexChartWidth() {
      return this.options.xaxis.categories.length * 110 + 'px'
    },
    previousPeriodData() {
      return this.sortMetrics ? this.sortMetrics.map((metric) => (metric.reference ? metric.reference[this.tabsValues[this.tab]] : 0)) : []
    },
    offeredPeriodData() {
      return this.sortMetrics ? this.sortMetrics.map((metric) => (metric.total ? metric.total[this.tabsValues[this.tab]] : 0)) : []
    },
    sortMetrics() {
      if (this.metrics) {
        const referenceValues = this.metrics.length > 100 ? this.metrics.slice(0, 100) : this.metrics
        return referenceValues.sort((a, b) => {
          return b.total[this.tabsValues[this.tab]] - a.total[this.tabsValues[this.tab]]
        })
      } else return []
    },
    formattedProducts() {
      return this.sortMetrics ? this.sortMetrics.map((metric) => metric.product.name) : []
    },
    tooltip() {
      return {
        custom: ({ series, seriesIndex, dataPointIndex, w }) => {
          const previousPeriodValue =
            this.tabsValues[this.tab] === 'revenue' ? 'R$' + this.localeString(series[0][dataPointIndex], 2) : series[0][dataPointIndex]
          const offeredPeriodValue =
            this.tabsValues[this.tab] === 'revenue' ? 'R$' + this.localeString(series[1][dataPointIndex], 2) : series[1][dataPointIndex]

          const productName = this.formattedProducts[dataPointIndex]

          const variation = series[0][dataPointIndex] ? (100 * (series[1][dataPointIndex] - series[0][dataPointIndex])) / series[0][dataPointIndex] : 0
          const variationColor = variation > 0 ? '#0A8533' : '#E03210'
          const plusVariationSignal = variation > 0 ? '+' : ''

          const periodsValuesComponent = `
                <div class="d-flex flex-row">
                  <span style="color: #31363B; font-weight: 600;">${productName}</span>
                  </div>
                <div class="d-flex flex-row">
                  <span style="color: #6B7580; margin-right: 4px;">Período anterior:</span>
                  <span style="color: #002040; font-weight: 700; ">${previousPeriodValue}</span>
                </div>
                <div class="d-flex flex-row">
                  <span style="color: #6B7580; margin-right: 4px;">Período ofertado:</span>
                  <span style="color: #00B395; font-weight: 700; ">${offeredPeriodValue}</span>
                </div>
                <div class="d-flex flex-row">
                  <span style="color: #6B7580; margin-right: 4px;">Variação:</span>
                  <span style="color: ${variation === 0 ? '#4B525A' : variationColor}; font-weight: 700; ">
                    ${plusVariationSignal + this.localeString(variation, 1)}
                  %</span>
                </div>
            `

          return `
                  <v-card class="px-3 py-2">
                    <v-card-text>
                      ${periodsValuesComponent}
                      <child-component></child-component>
                    </v-card-text>
                  </v-card>
                `
        },
      }
    },
  },
  mounted() {
    if (this.metrics?.length > 100) {
      const exportData = document.querySelector('#export-data')
      exportData.addEventListener('click', () => this.$emit('export-data'))
    }
  },
  methods: {
    localeString,
  },
}
</script>

<style lang="scss" scoped>
.apex-chart-section {
  overflow-x: auto;
  overflow-y: hidden;
}

::v-deep #export-data {
  font-weight: 400;
  color: $marca-0-l40;
  text-decoration: underline;
}
</style>
